/* eslint-disable no-unused-vars */
// lingland-frontend/src/pages/AdminTranslatorsPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import Select from 'react-select';
import { PlusCircle, Edit3, Trash2, Save, XCircle, AlertCircle, Loader2, Users } from 'lucide-react';

const reactSelectStyles = { /* Możesz skopiować style z innego pliku admina */ };

function AdminTranslatorsPage() {
    const [translators, setTranslators] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const token = localStorage.getItem('adminToken');

    const [isAdding, setIsAdding] = useState(false);
    const [editingTranslator, setEditingTranslator] = useState(null);
    const [formState, setFormState] = useState({
        first_name: '', last_name: '', email: '', notes: '', language_codes: []
    });
    const [formError, setFormError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const languageOptions = useMemo(() => languages.map(lang => ({ value: lang.code, label: lang.name })), [languages]);

    const fetchData = useCallback(async () => {
        setIsLoading(true); setError('');
        if (!token) { navigate('/admin/login'); return; }
        try {
            const [transRes, langsRes] = await Promise.all([
                axios.get(`${apiUrl}/api/admin/translators`, { headers: { 'x-auth-token': token } }),
                axios.get(`${apiUrl}/api/admin/languages`, { headers: { 'x-auth-token': token } })
            ]);
            setTranslators(transRes.data);
            setLanguages(langsRes.data);
        } catch (err) { setError(err.response?.data?.errors?.[0]?.msg || 'Nie udało się pobrać danych.'); if (err.response?.status === 401) navigate('/admin/login'); }
        finally { setIsLoading(false); }
    }, [apiUrl, navigate, token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormState(prev => ({ ...prev, [name]: value }));
        setFormError('');
    };

    const handleLangSelectChange = (selectedOptions) => {
        setFormState(prev => ({ ...prev, language_codes: selectedOptions ? selectedOptions.map(opt => opt.value) : [] }));
        setFormError('');
    };

    const resetForm = () => {
        setFormState({ first_name: '', last_name: '', email: '', notes: '', language_codes: [] });
        setIsAdding(false);
        setEditingTranslator(null);
        setFormError('');
    };

    const startAdd = () => { resetForm(); setIsAdding(true); };
    const startEdit = (translator) => {
        setEditingTranslator(translator);
        setFormState({
            first_name: translator.first_name,
            last_name: translator.last_name,
            email: translator.email,
            notes: translator.notes || '',
            is_active: translator.is_active,
            language_codes: translator.languages.map(l => l.code)
        });
        setIsAdding(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsProcessing(true); setFormError('');
        
        const url = editingTranslator
            ? `${apiUrl}/api/admin/translators/${editingTranslator.id}`
            : `${apiUrl}/api/admin/translators`;
        const method = editingTranslator ? 'put' : 'post';

        try {
            await axios[method](url, formState, { headers: { 'x-auth-token': token } });
            resetForm();
            fetchData();
        } catch (err) {
            setFormError(err.response?.data?.errors?.[0]?.msg || 'Wystąpił błąd.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDelete = async (translatorId) => {
        if (!window.confirm(`Czy na pewno chcesz usunąć tego tłumacza?`)) return;
        try {
            await axios.delete(`${apiUrl}/api/admin/translators/${translatorId}`, { headers: { 'x-auth-token': token } });
            fetchData();
        } catch (err) {
            alert(err.response?.data?.errors?.[0]?.msg || 'Nie udało się usunąć tłumacza.');
        }
    };

    const selectedLanguageOptions = useMemo(() => {
        return languageOptions.filter(opt => formState.language_codes.includes(opt.value));
    }, [formState.language_codes, languageOptions]);

    return (
        <section className="admin-page section-bg-white" style={{padding: "40px 0", minHeight: 'calc(100vh - 150px)'}}>
            <div className="container">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem'}}>
                    <h1><Users style={{marginRight: '0.5rem'}}/>Zarządzanie Tłumaczami</h1>
                    <Link to="/admin/dashboard" className="button-secondary button-small-outline">← Wróć do Dashboardu</Link>
                </div>

                {!isAdding && !editingTranslator && (
                    <button onClick={startAdd} className="button-primary" style={{marginBottom: '1.5rem'}}>
                        <PlusCircle size={18} style={{marginRight: '0.5rem'}}/> Dodaj Nowego Tłumacza
                    </button>
                )}

                {(isAdding || editingTranslator) && (
                    <form onSubmit={handleSubmit} className="admin-edit-form" style={{marginBottom: '2rem'}}>
                        <h3>{editingTranslator ? `Edytuj Tłumacza: ${editingTranslator.first_name}` : 'Dodaj Nowego Tłumacza'}</h3>
                        {formError && <div className="submit-message error form-message"><AlertCircle size={18}/> {formError}</div>}
                        <div className="form-row">
                            <div className="form-group"><label>Imię *</label><input type="text" name="first_name" value={formState.first_name} onChange={handleFormChange} required /></div>
                            <div className="form-group"><label>Nazwisko *</label><input type="text" name="last_name" value={formState.last_name} onChange={handleFormChange} required /></div>
                        </div>
                        <div className="form-group"><label>Email *</label><input type="email" name="email" value={formState.email} onChange={handleFormChange} required /></div>
                        <div className="form-group"><label>Języki *</label><Select options={languageOptions} isMulti value={selectedLanguageOptions} onChange={handleLangSelectChange} placeholder="Wybierz języki..." styles={reactSelectStyles}/></div>
                        <div className="form-group"><label>Notatki</label><textarea name="notes" value={formState.notes} onChange={handleFormChange}></textarea></div>
                        <div style={{marginTop: '1rem', display: 'flex', gap: '1rem'}}>
                            <button type="submit" className="button-primary" disabled={isProcessing}>
                                {isProcessing ? <Loader2 size={18} className="spinner"/> : <Save size={18}/>}
                                {editingTranslator ? 'Zapisz Zmiany' : 'Dodaj Tłumacza'}
                            </button>
                            <button type="button" onClick={resetForm} className="button-secondary" disabled={isProcessing}>Anuluj</button>
                        </div>
                    </form>
                )}

                {error && <div className="submit-message error" style={{ marginBottom: '1rem'}}>{error}</div>}
                {isLoading ? <p>Ładowanie...</p> : (
                    <div className="table-responsive-wrapper">
                        <table className="admin-table">
                            <thead><tr><th>Imię i Nazwisko</th><th>Email</th><th>Języki</th><th>Aktywny</th><th>Akcje</th></tr></thead>
                            <tbody>
                                {translators.map(t => (
                                    <tr key={t.id}>
                                        <td>{t.first_name} {t.last_name}</td><td>{t.email}</td>
                                        <td>{t.languages.map(l => l.name).join(', ')}</td>
                                        <td><span className={`status-badge ${t.is_active ? 'status-completed' : 'status-rejected'}`}>{t.is_active ? 'Tak' : 'Nie'}</span></td>
                                        <td>
                                            <button onClick={() => startEdit(t)} className="button-small-outline" style={{marginRight: '0.5rem'}}><Edit3 size={14}/> Edytuj</button>
                                            <button onClick={() => handleDelete(t.id)} className="button-small-outline button-danger"><Trash2 size={14}/> Usuń</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </section>
    );
}
export default AdminTranslatorsPage;